/** Thin wrapper around fetch that knows where the API lives.
 *
 * - In dev: Vite proxy at `/api` → http://localhost:8200 (see vite.config.ts).
 * - In prod: API lives at https://api.pencilout.app and the admin
 *   portal at https://admin.pencilout.app. Set the API base via
 *   the `VITE_API_BASE` env var at build time.
 *
 * Auth is via session cookies set by /auth/magic-link/consume —
 * `credentials: "include"` on every request so the cookie is sent
 * cross-subdomain (admin → api). The cookie is HttpOnly + scoped to
 * Domain=.pencilout.app so JS can't read it directly.
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export const apiBase = API_BASE;

/** Bearer-token getter registered by the Clerk bridge in main.tsx.
 *
 * The fetch wrapper is a plain module with no access to React
 * context, so the Clerk layer hands us its ``getToken`` at mount.
 * When registered (Clerk enabled + loaded), every request carries
 * ``Authorization: Bearer <session JWT>``; when null, requests fall
 * back to the legacy session cookie alone. Server accepts both.
 */
type AuthTokenGetter = () => Promise<string | null>;

let authTokenGetter: AuthTokenGetter | null = null;

export function setAuthTokenGetter(fn: AuthTokenGetter | null): void {
  authTokenGetter = fn;
}

async function bearerToken(): Promise<string | null> {
  if (!authTokenGetter) return null;
  try {
    return await authTokenGetter();
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  detail: string;
  /** Server-side error code, when the API returns a structured
   *  ``detail = {error, message}`` payload. Lets call sites switch on
   *  the code (e.g. "expired" vs "consumed" vs "free_tier_quota_exceeded")
   *  for tailored UX without parsing the human-readable message. */
  errorCode: string | null;

  constructor(status: number, detail: string, errorCode: string | null = null) {
    super(`API ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
    this.errorCode = errorCode;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await bearerToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = res.statusText;
    let errorCode: string | null = null;
    try {
      const data = (await res.json()) as {
        detail?: unknown;
      };
      detail = formatErrorDetail(data?.detail) || res.statusText;
      // Structured ``detail = {error, message}`` lets us pull the code.
      if (data?.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) {
        const obj = data.detail as Record<string, unknown>;
        if (typeof obj.error === "string") errorCode = obj.error;
      }
    } catch {
      /* non-JSON error body — keep statusText */
    }
    throw new ApiError(res.status, detail, errorCode);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Coerce a FastAPI error ``detail`` into a string we can render.
 *
 * Custom raises use a string (``HTTPException(422, "human message")``)
 * — easy. Pydantic validation errors come back as a list of
 * ``{loc, msg, type}`` objects; React would crash if we tried to
 * render that array directly. Flatten to ``"field: message"`` lines.
 *
 * Structured detail with an ``error`` + ``message`` shape (e.g. quota
 * errors, magic-link errors) renders to just ``message`` — the
 * machine-readable ``error`` is exposed separately on
 * ``ApiError.errorCode``. */
function formatErrorDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (!detail) return "";
  if (Array.isArray(detail)) {
    return detail
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") {
          const obj = entry as Record<string, unknown>;
          const loc = Array.isArray(obj.loc)
            ? (obj.loc as unknown[]).slice(1).join(".")
            : undefined;
          const msg = typeof obj.msg === "string" ? obj.msg : JSON.stringify(obj);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return JSON.stringify(entry);
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
  }
  return JSON.stringify(detail);
}

export const apiClient = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
